import React, { memo, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import PromiseWait from "../../../../PromiseWait"
import { IMedicalQuestionnaireProps } from "../../../models";
import { publicRoutes } from "../../../../../../routePaths";
import { getMedicalQuestionnaire } from '../../../../../../stores/medicalQuestionnariesStore';
import { assertNonNil } from '../../../../../../utils/objectUtils';
import { IMedicalQuestionnaireQuestion } from 'csd.phoenix.models/MedicalQuestionnaireQuestion';
import { useFormikContext, getIn } from 'formik';
import { IPrefillData } from '../../../../../../stores/formsStore';
import { IMedicalQuestionnaireQuestionGroup } from 'csd.phoenix.models';
import { H5 } from '@blueprintjs/core';
import MedQuestionField from './QuestionField';
import { IValidation } from '../../../models/validation';
import { getCurrentTenantInstallationId } from '../../../../../../stores/tenantInstallationsStore';
import useFormContext from "../../../FormContext";
import { IEFormsMedicalQuestionnaireAnswerPrefill } from 'csd.phoenix.models/EFormsMedicalQuestionnaireAnswerPrefill';
import { IEFormsMedicalQuestionnairePrefill } from 'csd.phoenix.models/EFormsMedicalQuestionnairePrefill';


interface IGroupHeader extends Omit<IMedicalQuestionnaireQuestionGroup,
  "MedicalQuestionnaireQuestions" | "MedicalQuestionnaire" | "MedicalQuestionnaireId"> {
}

// Interface for grabbing tiId from URL if in public mode
interface IRouteParams {
  tiid: string;
}

function determineGroupHeaders(questions: IMedicalQuestionnaireQuestion[]) {
  const theGroups = questions
    .filter(g => g.MedicalQuestionnaireQuestionGroup?.GroupId! !== undefined)
    .map(h => h.MedicalQuestionnaireQuestionGroup);

  let theFilteredGroups: IGroupHeader[] = [];

  theGroups.forEach(group => {

    if (!theFilteredGroups.some(g => g.GroupId === group?.GroupId)) {

      theFilteredGroups.push(
        {
          GroupId: group?.GroupId!,
          Heading: group?.Heading!,
          GroupQuestionText: group?.GroupQuestionText!
        }
      );
    }
  })

  return theFilteredGroups;
}

function filterQuestions(questions: IMedicalQuestionnaireQuestion[], answers: IEFormsMedicalQuestionnaireAnswerPrefill[])
  : IMedicalQuestionnaireQuestion[] {
  return questions.filter(q => answers.map(a => a.QuestionId).includes(q.RecordId!));
}

function GetTiidFromUrl(): string {
  return useParams<IRouteParams>().tiid;
};


const MedicalQuestionnaireField: React.FC<IMedicalQuestionnaireProps> = memo((props) => {
  const { values } = useFormikContext<IPrefillData>();
  const medQFromPrefill = getIn(values, props.binding) as IEFormsMedicalQuestionnairePrefill;
  const medicalQuestionnaireId = medQFromPrefill?.QuestionnaireId;
  const url = window.location.href;
  const isPublicUrl = Boolean(url.indexOf(publicRoutes.base) !== -1) // Is this a public url.
  const paramInstallationId = isPublicUrl ? GetTiidFromUrl() : undefined;
  const tenantInstallationId = assertNonNil(getCurrentTenantInstallationId() ?? paramInstallationId); // The public URL will include the tiid value. The internal one won't.
  const promise = useMemo(() => getMedicalQuestionnaire(tenantInstallationId, medicalQuestionnaireId), [tenantInstallationId, medicalQuestionnaireId]);

  return (
    <PromiseWait promise={promise} component={MedicalQuestionnaireFieldContent} />
  )
});

const MedicalQuestionnaireFieldContent: React.FC<{ promiseResult: IMedicalQuestionnaireQuestion[] }> = ({ promiseResult: questions }) => {

  const { values } = useFormikContext<IPrefillData>();
  const { isEditing } = useFormContext();
  const medicalQuestionnaireId = questions[0].MedicalQuestionnaireId;
  const validations: IValidation[] = [];
  const groups = determineGroupHeaders(questions);

  useEffect(() => {
    if (values?.Patient?.MedicalQuestionnaire) {
      values.Patient.MedicalQuestionnaire.QuestionnaireId = medicalQuestionnaireId;
    }
  }, [medicalQuestionnaireId, values]);

  if (!isEditing && values?.Patient?.MedicalQuestionnaire?.Questions)
    questions = filterQuestions(questions, values.Patient.MedicalQuestionnaire.Questions);


  if (groups instanceof Array && groups.length > 0) {
    return (
      <div>
        {groups.map(group => {
          return (
            <div key={group?.GroupId}>
              <H5 style={{ marginBottom: "0.5em" }}>{group?.Heading}</H5>
              <H5 style={{ marginBottom: "0.5em" }}>{group?.GroupQuestionText}</H5>
              {questions && questions.map((q, i) => {
                if (group?.GroupId === q.GroupId) {
                  return (
                    <div key={i}>
                      <MedQuestionField
                        label={q.Text}
                        questionId={q.RecordId}
                        requiresNoteIfYes={(q.QuestionType === 2) ? true : false}
                        binding={"Patient.MedicalQuestionnaire.Questions[" + (i) + "]"}
                        validations={validations}
                      >
                      </MedQuestionField>
                    </div>
                  )
                }
                return null;
              })}
            </div>
          )
        })}
      </div>
    );
  }
  else {
    return (
      <div>
        {questions && questions.map((q, i) => {
          return (
            <div key={i}>
              <MedQuestionField
                label={q.Text}
                questionId={q.RecordId}
                requiresNoteIfYes={(q.QuestionType === 2) ? true : false}
                binding={"Patient.MedicalQuestionnaire.Questions[" + (i) + "]"}
                validations={validations}
              >
              </MedQuestionField>
            </div>
          )
        }
        )}
      </div>
    );
  }
}

export default MedicalQuestionnaireField;
